import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { AuthService } from '../service/auth.service';

/**
 * Expulsa al usuario a la portada en cuanto la sesión deja de servir:
 *
 *  - antes de enviar, si la petición iba autenticada pero no hay token o ya caducó;
 *  - al recibir, si la API responde con un estado de sesión inválida.
 *
 * El middleware de la API responde 402 tanto si falta la cabecera Authorization
 * como si el usuario del token no existe, así que 402 cuenta como sesión muerta.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  /** Estados que significan "esta sesión ya no vale". */
  private readonly estadosSesionInvalida = [401, 402, 403];

  /**
   * Endpoints que se usan SIN sesión iniciada (login, registro, recuperación).
   * Un error suyo no debe expulsar a nadie: el login devuelve 400 con una
   * contraseña incorrecta y eso no es una sesión caducada.
   */
  private readonly rutasPublicas = ['/api/practicas-autenticacion/'];

  constructor(private auth: AuthService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    if (this.requiereSesion(req) && !this.auth.isAuthenticated()) {
      this.auth.logout('no hay sesion valida para ' + req.url);

      return throwError(() => new HttpErrorResponse({
        status: 401,
        statusText: 'Sesion no valida',
        url: req.url
      }));
    }

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {

        if (this.esSesionInvalida(req, error)) {
          this.auth.logout('la API respondio ' + error.status + ' en ' + req.url);
        }

        return throwError(() => error);
      })
    );
  }

  /**
   * Una petición requiere sesión si el servicio le puso la cabecera Authorization.
   * Cuando no hay token, api.service manda igualmente "Bearer null", así que este
   * caso también queda cubierto.
   */
  private requiereSesion(req: HttpRequest<any>): boolean {
    return req.headers.has('Authorization') && !this.esRutaPublica(req);
  }

  private esSesionInvalida(req: HttpRequest<any>, error: HttpErrorResponse): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }

    if (this.esRutaPublica(req)) {
      return false;
    }

    return this.estadosSesionInvalida.indexOf(error.status) !== -1;
  }

  private esRutaPublica(req: HttpRequest<any>): boolean {
    for (let i = 0; i < this.rutasPublicas.length; i++) {
      if (req.url.indexOf(this.rutasPublicas[i]) !== -1) {
        return true;
      }
    }

    return false;
  }
}
